import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../LanguageToggle";
import CyberBrand from "../common/CyberBrand";

/*
 * Cyber-Luxe two-column login shell. Left pane = brand + serif headline +
 * tagline + compliance badges (md+). Right pane = the form card.
 *
 * Children render inside the form card. Page-level chrome (theme toggle,
 * forgot-password modal) stays in the host component.
 */
export default function AuthShell({
  tagline,
  title,
  subtitle,
  badges = ["AI-powered", "Topology-Aware", "Framework Alignment"],
  formEyebrow,
  formTitle,
  formSubtitle,
  formMaxWidthClass = "max-w-md",
  children,
}) {
  const { t, i18n } = useTranslation();
  const shellRef = useRef(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  useEffect(() => {
    const currentShell = shellRef.current;
    if (!currentShell) return;

    // Throttle to one update per animation frame — mousemove fires far more
    // often than the display refreshes, and each update repaints the
    // mouse-tracked cyber-grid mask. rAF coalesces bursts into one paint.
    let frame = 0;
    let nextX = 0;
    let nextY = 0;
    const handleMouseMove = (e) => {
      const rect = currentShell.getBoundingClientRect();
      nextX = e.clientX - rect.left;
      nextY = e.clientY - rect.top;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        currentShell.style.setProperty("--mouse-x", `${nextX}px`);
        currentShell.style.setProperty("--mouse-y", `${nextY}px`);
      });
    };

    currentShell.addEventListener("mousemove", handleMouseMove);
    return () => {
      currentShell.removeEventListener("mousemove", handleMouseMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      ref={shellRef}
      dir="ltr"
      className="auth-portal min-h-screen grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden bg-(--color-bg) transition-colors duration-300"
    >
      {/* Background Interactive Cyber Grid & Ambient Glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="cyber-grid" />
        

        <div className="glow-blob glow-blob-purple absolute top-[-10%] right-[-10%]" />
        <div className="glow-blob glow-blob-green absolute bottom-[-10%] left-[-15%]" />
        <div className="mouse-glow" />
      </div>

      <LanguageToggle lang={i18n.language} toggleLanguage={toggleLanguage} />

      {/* Brand pane — desktop only */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative z-10">
        <CyberBrand size="lg" showSubtitle={true} />

        <div>
          {tagline && (
            <div className="text-[11px] tracking-[0.28em] uppercase mb-4 text-(--color-primary) flex items-center gap-2 font-semibold">
              <span className="h-[1px] w-6 bg-(--color-primary) inline-block" />
              {tagline}
            </div>
          )}
          <h1 className="qv-serif text-[56px] leading-[0.98] font-medium tracking-tight text-(--color-text-primary)">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-6 max-w-md text-[14px] leading-relaxed text-(--color-text-secondary)">
              {subtitle}
            </p>
          )}

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            {badges.map((c) => (
              <div
                key={c}
                className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--color-bg-card)] border border-[var(--color-border-solid)] text-[9.5px] tracking-[0.18em] uppercase text-(--color-text-secondary) font-medium shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:border-[var(--color-primary)] hover:shadow-[0_0_12px_var(--color-primary-50)]"
              >
                <span className="cyber-badge-dot" />
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] text-(--color-text-muted)">
          {t("auth.shell.footer", { year: new Date().getFullYear() })}
        </div>
      </div>

      {/* Form pane */}
      <div className="flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className={`w-full ${formMaxWidthClass} space-y-6 lg:space-y-0`}>
          {(tagline || title || subtitle) && (
            <div className="lg:hidden">
              {/* Mobile brand */}
              <div className="mb-6">
                <CyberBrand size="md" showSubtitle={false} />
              </div>

              {tagline && (
                <div className="text-[10px] tracking-[0.28em] uppercase mb-3 text-(--color-primary)">
                  ── {tagline}
                </div>
              )}
              <div className="qv-serif text-[32px] sm:text-[36px] leading-[1.05] font-medium tracking-tight text-(--color-text-primary)">
                {title}
              </div>
            </div>
          )}

          <div className="qv-card qv-card-login qv-noise w-full p-7 sm:p-9 relative">
            <div className="cyber-line-accent" />
            {formEyebrow && (
              <div className="text-[10.5px] tracking-[0.28em] uppercase text-(--color-text-muted)">
                {formEyebrow}
              </div>
            )}
            {formTitle && (
              <h2 className="qv-serif mt-2 text-[30px] sm:text-[32px] font-medium tracking-tight text-(--color-text-primary)">
                {formTitle}
              </h2>
            )}
            {formSubtitle && (
              <p className="mt-1 text-[13px] text-(--color-text-secondary)">
                {formSubtitle}
              </p>
            )}

            <div className="mt-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
